import {
  useCallback,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";

import {
  LockPosition,
  ProposedRoute,
  RouteColorName,
  RouteBuilderProperty,
  RouteBuilderSession,
  RouteEndpointLocation,
} from "../../../../../types/routeBuilder";
import { multipleChoiceConfirmWrapper } from "../../../../../components/Modals/MultipleChoiceConfirmWrapper";
import routeBuilder from "../../../../../services/axios/routeBuilder/index";
import { flashRouteBuilderError } from "../lib/flashRouteBuilderError";
import {
  addPropertyVisit as addPropertyVisitOp,
  addPropertiesToUnassigned as addPropertiesToUnassignedOp,
  addRoute as addRouteOp,
  assignPropertyVisitToRoute as assignPropertyVisitToRouteOp,
  movePropertyVisit as movePropertyVisitOp,
  locatePropertyVisit,
  nextDirtyRouteIds,
  removePropertyVisit as removePropertyVisitOp,
  removeRoute as removeRouteOp,
  renameRoute as renameRouteOp,
  reorderRoutes as reorderRoutesOp,
  setRouteColor as setRouteColorOp,
  setRouteInstructions as setRouteInstructionsOp,
  setRouteEndpoint as setRouteEndpointOp,
  setPropertyVisitLock,
  unassignPropertyVisit as unassignPropertyVisitOp,
  UNASSIGNED_ROUTE_ID,
  updatePropertyConstraints,
  type PropertyPatch,
} from "../lib/sessionOps";
import { needsRouteFocus, sanitizeFocusedRouteIds } from "../lib/routeFocus";
import type { ViewMode } from "../types/routeBuilderTypes";
import {
  defaultAddUnassignedFilters,
  type AddUnassignedFilters,
} from "../lib/addUnassignedCatalog";
import { hasValidRouteEndpoint } from "../lib/routeValidation";

export type { PropertyPatch };

type Args = {
  setIsBusy: Dispatch<SetStateAction<boolean>>;
  guardOptimization: () => boolean;
  handleOptimizationError: (err: unknown, fallbackPrefix: string) => void;
  refreshAccountUsage: () => void;
};

export function useRouteBuilderSession({
  setIsBusy,
  guardOptimization,
  handleOptimizationError,
  refreshAccountUsage,
}: Args) {
  const [session, setSession] = useState<RouteBuilderSession | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    // innerWidth is 0 on some first paints (Playwright CT iframe). Treat that
    // as desktop so we don't start in list view and race the split switch.
    const width = typeof window === "undefined" ? 0 : window.innerWidth;
    return width > 0 && width < 768 ? "list" : "split";
  });
  const [selectedRouteId, setSelectedRouteId] = useState<string | null>(null);
  const [selectedPropertyVisitId, setSelectedPropertyVisitId] = useState<
    string | null
  >(null);
  const [dirtyRouteIds, setDirtyRouteIds] = useState<Set<string>>(new Set());
  const [hasUnsavedSessionChanges, setHasUnsavedSessionChanges] =
    useState(false);
  const [focusedRouteIds, setFocusedRouteIds] = useState<string[] | null>(null);
  const [mapHiddenRouteIds, setMapHiddenRouteIds] = useState<Set<string>>(
    () => new Set(),
  );
  /** Bumped when a property is opened from the map so the list can scroll/flash. */
  const [listRevealNonce, setListRevealNonce] = useState(0);
  // Remembered only while this editor is open. Closing it starts a new editing session.
  const skipMoveToUnassignedConfirm = useRef(false);
  const openUnassignedPanelRef = useRef<() => void>(() => {});
  const [catalogFilters, setCatalogFilters] = useState(() => ({
    sessionId: session?.id,
    filters: defaultAddUnassignedFilters(),
  }));
  // Popup filters survive closing the panel, but never carry into another session.
  if (catalogFilters.sessionId !== session?.id) {
    setCatalogFilters({
      sessionId: session?.id,
      filters: defaultAddUnassignedFilters(),
    });
  }
  const setAddUnassignedFilters = useCallback(
    (patch: Partial<AddUnassignedFilters>) => {
      setCatalogFilters((previous) => ({
        ...previous,
        filters: { ...previous.filters, ...patch },
      }));
    },
    [],
  );
  /** Bumped on pin clicks so the map can pan even when the visit is already selected. */
  const [mapFocusNonce, setMapFocusNonce] = useState(0);
  const [mapFocusPropertyVisitId, setMapFocusPropertyVisitId] = useState<
    string | null
  >(null);

  const markSessionDirty = useCallback(() => {
    setHasUnsavedSessionChanges(true);
  }, []);

  /**
   * Save an edited session. Marks routes whose stops or endpoints changed as
   * needing re-optimization. Returns false when the edit changed nothing.
   */
  const commitSession = useCallback(
    (next: RouteBuilderSession) => {
      if (!session || next === session) return false;
      setSession(next);
      setDirtyRouteIds((prev) => nextDirtyRouteIds(prev, session, next));
      markSessionDirty();
      return true;
    },
    [session, markSessionDirty],
  );

  const needsRouteFocusMode = needsRouteFocus(session?.routes.length ?? 0);

  const effectiveFocusedRouteIds = useMemo(() => {
    if (!session || !needsRouteFocusMode) return null;
    return sanitizeFocusedRouteIds(
      focusedRouteIds,
      session.routes.map((route) => route.id),
    );
  }, [focusedRouteIds, needsRouteFocusMode, session]);

  const visibleRoutes = useMemo((): ProposedRoute[] => {
    if (!session) return [];
    if (!effectiveFocusedRouteIds?.length) return session.routes;
    const focused = new Set(effectiveFocusedRouteIds);
    const subset = session.routes.filter((route) => focused.has(route.id));
    return subset.length > 0 ? subset : session.routes;
  }, [session, effectiveFocusedRouteIds]);

  const mapVisibleRoutes = useMemo((): ProposedRoute[] => {
    if (mapHiddenRouteIds.size === 0) return visibleRoutes;
    return visibleRoutes.filter((route) => !mapHiddenRouteIds.has(route.id));
  }, [visibleRoutes, mapHiddenRouteIds]);

  const toggleRouteMapVisibility = useCallback((routeId: string) => {
    setMapHiddenRouteIds((prev) => {
      const next = new Set(prev);
      if (next.has(routeId)) next.delete(routeId);
      else next.add(routeId);
      return next;
    });
  }, []);

  const isRouteMapVisible = useCallback(
    (routeId: string) => !mapHiddenRouteIds.has(routeId),
    [mapHiddenRouteIds],
  );

  const selectPropertyFromMap = useCallback(
    (routeId: string, propertyVisitId: string) => {
      // Opening an unassigned visit should preserve the currently selected route.
      if (routeId !== UNASSIGNED_ROUTE_ID) setSelectedRouteId(routeId);
      setSelectedPropertyVisitId(propertyVisitId);
      setListRevealNonce((n) => n + 1);
    },
    [],
  );

  const focusPropertyOnMap = useCallback((propertyVisitId: string) => {
    setMapFocusPropertyVisitId(propertyVisitId);
    setMapFocusNonce((n) => n + 1);
  }, []);
  const setFocusedRoutes = useCallback(
    (ids: string[] | null) => {
      if (!session) {
        setFocusedRouteIds(null);
        return;
      }
      const next = sanitizeFocusedRouteIds(
        ids,
        session.routes.map((route) => route.id),
      );
      setFocusedRouteIds(next);
      if (next?.length && selectedRouteId && !next.includes(selectedRouteId)) {
        setSelectedRouteId(next[0]);
      }
    },
    [session, selectedRouteId],
  );

  const clearRouteFocus = useCallback(() => {
    setFocusedRouteIds(null);
  }, []);

  const reoptimizeRoute = useCallback(
    async (routeId: string) => {
      if (!session) return;
      if (!guardOptimization()) return;
      const route = session.routes.find(
        (candidate) => candidate.id === routeId,
      );
      if (
        !route ||
        !hasValidRouteEndpoint(route.startLocation) ||
        !hasValidRouteEndpoint(route.endLocation)
      ) {
        flashRouteBuilderError(
          new Error("Select start and end locations before re-optimizing."),
          "Failed to re-optimize route",
        );
        return;
      }
      setIsBusy(true);
      try {
        const next = await routeBuilder.drivingPath({
          session,
          routeIds: [routeId],
          optimize: true,
        });
        setSession(next);
        setDirtyRouteIds((prev) => {
          const copy = new Set(prev);
          copy.delete(routeId);
          return copy;
        });
        refreshAccountUsage();
      } catch (err) {
        console.error(err);
        handleOptimizationError(err, "Failed to re-optimize route");
      } finally {
        setIsBusy(false);
      }
    },
    [
      session,
      setIsBusy,
      guardOptimization,
      handleOptimizationError,
      refreshAccountUsage,
    ],
  );

  const reoptimizeAll = useCallback(async () => {
    if (!session) return;
    if (!guardOptimization()) return;
    setIsBusy(true);
    try {
      const next = await routeBuilder.drivingPath({
        session,
        routeIds: session.routes.map((r) => r.id),
        optimize: true,
      });
      setSession(next);
      setDirtyRouteIds(new Set());
      setSelectedRouteId(next.routes[0]?.id ?? null);
      refreshAccountUsage();
    } catch (err) {
      console.error(err);
      handleOptimizationError(err, "Failed to re-optimize routes");
    } finally {
      setIsBusy(false);
    }
  }, [
    session,
    setIsBusy,
    guardOptimization,
    handleOptimizationError,
    refreshAccountUsage,
  ]);

  const movePropertyVisit = useCallback(
    (
      fromRouteId: string,
      toRouteId: string,
      propertyVisitId: string,
      toIndex: number,
    ) => {
      if (!session) return;
      const moved = commitSession(
        movePropertyVisitOp(
          session,
          fromRouteId,
          toRouteId,
          propertyVisitId,
          toIndex,
        ),
      );
      if (
        moved &&
        toRouteId === UNASSIGNED_ROUTE_ID &&
        fromRouteId !== UNASSIGNED_ROUTE_ID
      ) {
        openUnassignedPanelRef.current();
      }
    },
    [session, commitSession],
  );

  const reorderRoutes = useCallback(
    (routeId: string, toIndex: number) => {
      if (!session) return;
      commitSession(reorderRoutesOp(session, routeId, toIndex));
    },
    [session, commitSession],
  );

  const renameRoute = useCallback(
    (routeId: string, name: string) => {
      if (!session) return;
      commitSession(renameRouteOp(session, routeId, name));
    },
    [session, commitSession],
  );

  const setRouteColor = useCallback(
    (routeId: string, color: RouteColorName) => {
      if (!session) return;
      commitSession(setRouteColorOp(session, routeId, color));
    },
    [session, commitSession],
  );

  const setRouteInstructions = useCallback(
    (routeId: string, instructions: string) => {
      if (!session) return;
      commitSession(setRouteInstructionsOp(session, routeId, instructions));
    },
    [session, commitSession],
  );

  const setRouteEndpoint = useCallback(
    (
      routeId: string,
      type: "start" | "end",
      location: RouteEndpointLocation,
    ) => {
      if (!session) return;
      commitSession(setRouteEndpointOp(session, routeId, type, location));
    },
    [session, commitSession],
  );

  const updateProperty = useCallback(
    (propertyId: string, patch: PropertyPatch) => {
      if (!session) return;
      commitSession(updatePropertyConstraints(session, propertyId, patch));
    },
    [session, commitSession],
  );

  const setLock = useCallback(
    (propertyVisitId: string, lock: LockPosition) => {
      if (!session) return;
      commitSession(setPropertyVisitLock(session, propertyVisitId, lock));
    },
    [session, commitSession],
  );

  const addPropertyVisit = useCallback(
    (routeId: string, property: RouteBuilderProperty, toIndex?: number) => {
      if (!session) return;
      commitSession(addPropertyVisitOp(session, routeId, property, toIndex));
    },
    [session, commitSession],
  );

  const assignPropertyVisitToRoute = useCallback(
    (propertyVisitId: string, routeId: string, patch: PropertyPatch = {}) => {
      if (!session) return;
      const next = assignPropertyVisitToRouteOp(
        session,
        propertyVisitId,
        routeId,
        { patch },
      );
      if (!commitSession(next)) return;
      if (routeId === UNASSIGNED_ROUTE_ID) {
        openUnassignedPanelRef.current();
      } else if (next.routes.some((route) => route.id === routeId)) {
        setSelectedRouteId(routeId);
      }
      setSelectedPropertyVisitId(null);
    },
    [session, commitSession],
  );

  const addPropertiesToUnassigned = useCallback(
    (properties: RouteBuilderProperty[]) => {
      if (!session) return;
      if (commitSession(addPropertiesToUnassignedOp(session, properties)))
        openUnassignedPanelRef.current();
    },
    [session, commitSession],
  );

  const removePropertyVisit = useCallback(
    (propertyVisitId: string) => {
      if (!session) return;
      if (!commitSession(removePropertyVisitOp(session, propertyVisitId))) {
        return;
      }
      if (selectedPropertyVisitId === propertyVisitId) {
        setSelectedPropertyVisitId(null);
      }
    },
    [session, selectedPropertyVisitId, commitSession],
  );

  const unassignPropertyVisit = useCallback(
    (propertyVisitId: string) => {
      if (!session) return;
      if (commitSession(unassignPropertyVisitOp(session, propertyVisitId))) {
        openUnassignedPanelRef.current();
      }
    },
    [session, commitSession],
  );

  const confirmMoveToUnassigned = useCallback(
    async ({ name, locked }: { name: string; locked: boolean }) => {
      if (skipMoveToUnassignedConfirm.current) return true;
      const confirmed = await multipleChoiceConfirmWrapper(
        "Move to Unassigned?",
        {
          description: locked
            ? `Move ${name} to Unassigned? This clears its lock. It stays in the plan, and this route will need to be re-optimized.`
            : `Move ${name} to Unassigned? It stays in the plan, and this route will need to be re-optimized.`,
          confirmLabel: "Move to Unassigned",
        },
      );
      if (confirmed) skipMoveToUnassignedConfirm.current = true;
      return confirmed;
    },
    [],
  );

  const resetMoveToUnassignedConfirm = useCallback(() => {
    skipMoveToUnassignedConfirm.current = false;
  }, []);

  const registerOpenUnassignedPanel = useCallback((open: () => void) => {
    openUnassignedPanelRef.current = open;
    return () => {
      if (openUnassignedPanelRef.current === open) {
        openUnassignedPanelRef.current = () => {};
      }
    };
  }, []);

  const addRoute = useCallback(() => {
    if (!session) return;
    const next = addRouteOp(session, selectedRouteId);
    if (!commitSession(next)) return;
    setSelectedRouteId(next.routes[next.routes.length - 1]?.id ?? null);
  }, [session, selectedRouteId, commitSession]);

  const removeRoute = useCallback(
    (routeId: string) => {
      if (!session) return;
      const next = removeRouteOp(session, routeId);
      if (!commitSession(next)) return;
      setMapHiddenRouteIds((prev) => {
        const copy = new Set(prev);
        copy.delete(routeId);
        return copy;
      });
      setFocusedRouteIds((prev) =>
        prev ? prev.filter((id) => id !== routeId) : null,
      );
      if (selectedRouteId === routeId) {
        setSelectedRouteId(next.routes[0]?.id ?? null);
      }
      if (
        selectedPropertyVisitId &&
        !locatePropertyVisit(next, selectedPropertyVisitId)
      ) {
        setSelectedPropertyVisitId(null);
      }
      if (next.unroutedJobs.length > session.unroutedJobs.length)
        openUnassignedPanelRef.current();
    },
    [session, selectedRouteId, selectedPropertyVisitId, commitSession],
  );

  const getProperty = useCallback(
    (propertyId: string) =>
      session?.availableProperties.find((p) => p.jobId === propertyId),
    [session],
  );

  const getPropertyForVisit = useCallback(
    (propertyVisitId: string) => {
      if (!session) return undefined;
      const propertyId = locatePropertyVisit(
        session,
        propertyVisitId,
      )?.propertyId;
      return session.availableProperties.find(
        (property) => property.jobId === propertyId,
      );
    },
    [session],
  );

  return {
    session,
    setSession,
    viewMode,
    setViewMode,
    selectedRouteId,
    setSelectedRouteId,
    selectedPropertyVisitId,
    setSelectedPropertyVisitId,
    listRevealNonce,
    selectPropertyFromMap,
    mapFocusNonce,
    mapFocusPropertyVisitId,
    focusPropertyOnMap,
    dirtyRouteIds,
    setDirtyRouteIds,
    hasUnsavedSessionChanges,
    setHasUnsavedSessionChanges,
    focusedRouteIds: effectiveFocusedRouteIds,
    setFocusedRouteIds,
    needsRouteFocusMode,
    visibleRoutes,
    mapVisibleRoutes,
    mapHiddenRouteIds,
    setMapHiddenRouteIds,
    toggleRouteMapVisibility,
    isRouteMapVisible,
    setFocusedRoutes,
    clearRouteFocus,
    reoptimizeRoute,
    reoptimizeAll,
    movePropertyVisit,
    addPropertyVisit,
    addPropertiesToUnassigned,
    addUnassignedFilters: catalogFilters.filters,
    setAddUnassignedFilters,
    assignPropertyVisitToRoute,
    removePropertyVisit,
    unassignPropertyVisit,
    confirmMoveToUnassigned,
    resetMoveToUnassignedConfirm,
    registerOpenUnassignedPanel,
    addRoute,
    removeRoute,
    reorderRoutes,
    renameRoute,
    setRouteColor,
    setRouteInstructions,
    setRouteEndpoint,
    updateProperty,
    setLock,
    getProperty,
    getPropertyForVisit,
  };
}

export { UNASSIGNED_ROUTE_ID };
